import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useErpCareerExerciseVocabularyQuery } from 'core/graphql';

import { IExerciseBinding, IExerciseFieldKind } from './careerModel';

const EMPTY_KINDS: IExerciseFieldKind[] = [];
const EMPTY_BINDINGS: IExerciseBinding[] = [];

interface IExerciseVocabularyHook {
  fieldTypes: IExerciseFieldKind[];
  bindings: IExerciseBinding[];
  /** Какие типы поля принимает привязка. Пусто — привязка неизвестна. */
  typesFor: (binding: string) => string[];
  bindingsFor: (fieldType: string) => IExerciseBinding[];
  maxFields: number;
  maxOptions: number;
  loading: boolean;
}

/**
 * ТЗ 0039 — словарь конструктора анкет, ведомый сервером.
 *
 * 🔴 Матрица «тип поля × привязка» намеренно НЕ лежит копией в этом репозитории.
 * Копия расходится с серверной валидацией молча: конструктор предлагает пару,
 * оператор её выбирает, сохранение отбивает — и виноватым выглядит сохранение.
 * Гейт на равенство двух матриц живёт в `test_exercise.py`.
 *
 * Подписи при этом ЛОКАЛИЗУЮТСЯ здесь: сервер отдаёт их по-русски, а ERP у
 * владельца работает на польском, и наполовину русский экран методиста —
 * ровно то, что ловится только глазами. Серверная подпись остаётся запасной,
 * поэтому новый тип, для которого ключ ещё не завели, покажется по-русски,
 * а не путём ключа i18n.
 *
 * `cache-first`: словарь меняется только вместе с выкладкой бэкенда, и
 * перезапрашивать его на каждое открытие карточки незачем.
 */
/** Точка — разделитель уровней в i18next, поэтому в ключе её быть не может. */
const labelKey = (value: string) => value.replace(/\./g, '_') || 'none';

export const useExerciseVocabulary = (): IExerciseVocabularyHook => {
  const { t } = useTranslation();
  const { data, loading } = useErpCareerExerciseVocabularyQuery({
    fetchPolicy: 'cache-first',
  });

  const vocabulary = data?.careerExerciseVocabulary;

  const fieldTypes = useMemo(
    () =>
      (vocabulary?.fieldTypes ?? EMPTY_KINDS).map((item) => ({
        ...item,
        label: t(`pages.careerCenter.exercise.type.${labelKey(item.value)}`, {
          defaultValue: item.label,
        }),
      })),
    [vocabulary, t],
  );

  const bindings = useMemo(
    () =>
      (vocabulary?.bindings ?? EMPTY_BINDINGS).map((item) => ({
        ...item,
        label: t(`pages.careerCenter.exercise.target.${labelKey(item.value)}`, {
          defaultValue: item.label,
        }),
      })),
    [vocabulary, t],
  );

  const byBinding = useMemo(
    () =>
      bindings.reduce<Record<string, string[]>>((acc, item) => {
        acc[item.value] = [...item.fieldTypes];
        return acc;
      }, {}),
    [bindings],
  );

  return useMemo(
    () => ({
      fieldTypes,
      bindings,
      typesFor: (binding: string) => byBinding[binding] ?? [],
      bindingsFor: (fieldType: string) =>
        bindings.filter((item) => item.fieldTypes.includes(fieldType)),
      // Пределы приходят с сервера по той же причине, что и матрица: цифра,
      // вписанная сюда руками, однажды разойдётся с той, что отбивает.
      maxFields: vocabulary?.maxFields ?? 0,
      maxOptions: vocabulary?.maxOptions ?? 0,
      loading,
    }),
    [vocabulary, fieldTypes, bindings, byBinding, loading],
  );
};
